import {
  IconActivity,
  IconBeach,
  IconBuildingStore,
  IconCalendar,
  IconCalendarTime,
  IconCreditCard,
  IconChartBar,
  IconChecklist,
  IconClock,
  IconDeviceTablet,
  IconFileInvoice,
  IconFolders,
  IconLayoutDashboard,
  IconReceipt,
  IconSettings,
  IconTable,
  IconTags,
  IconUsers,
  type Icon,
} from '@tabler/icons-react';
import type { Feature } from '@/api/workspace';
import type { CurrentWorkspace } from './WorkspaceContext';

export interface NavItem {
  /** i18n-Schlüssel unter `nav.items.*` */
  key: string;
  path: string;
  icon: Icon;
  /** Release-Phase laut docs/02-architektur-und-plan.md (für Platzhalter) */
  phase: number;
  /** Feature-IDs aus docs/01-feature-spezifikation.md */
  features: string;
  /** Benötigtes Tarif-/Modul-Feature (UI-04) */
  feature?: Feature;
  /** Sichtbarkeit nach Rolle */
  visible?: (ctx: CurrentWorkspace) => boolean;
}

export interface NavGroup {
  key: 'track' | 'analyze' | 'manage';
  items: NavItem[];
}

const managers = (ctx: CurrentWorkspace) => ctx.isManager;
const admins = (ctx: CurrentWorkspace) => ctx.isAdmin;
const owners = (ctx: CurrentWorkspace) => ctx.isOwner;

/** Seitennavigation (UI-02). */
export const navigation: NavGroup[] = [
  {
    key: 'track',
    items: [
      { key: 'timesheet', path: '/timesheet', icon: IconTable, phase: 2, features: 'TS-01 – TS-10', feature: 'TIMESHEET' },
      { key: 'tracker', path: '/tracker', icon: IconClock, phase: 1, features: 'TT-01 – TT-18' },
      { key: 'calendar', path: '/calendar', icon: IconCalendar, phase: 2, features: 'CAL-01 – CAL-08', feature: 'CALENDAR' },
      { key: 'schedule', path: '/schedule', icon: IconCalendarTime, phase: 5, features: 'SCH-01 – SCH-07', feature: 'SCHEDULING' },
      { key: 'expenses', path: '/expenses', icon: IconReceipt, phase: 5, features: 'EXP-01 – EXP-05', feature: 'EXPENSES' },
      { key: 'timeOff', path: '/time-off', icon: IconBeach, phase: 4, features: 'OFF-01 – OFF-08', feature: 'TIME_OFF' },
    ],
  },
  {
    key: 'analyze',
    items: [
      { key: 'dashboard', path: '/dashboard', icon: IconLayoutDashboard, phase: 1, features: 'DASH-01 – DASH-03' },
      { key: 'reports', path: '/reports', icon: IconChartBar, phase: 1, features: 'REP-01 – REP-19' },
      { key: 'activity', path: '/activity', icon: IconActivity, phase: 5, features: 'ACT-01 – ACT-04', feature: 'ACTIVITY' },
    ],
  },
  {
    key: 'manage',
    items: [
      { key: 'kiosks', path: '/kiosks', icon: IconDeviceTablet, phase: 3, features: 'KIO-01 – KIO-06', feature: 'KIOSK', visible: managers },
      { key: 'approvals', path: '/approvals', icon: IconChecklist, phase: 4, features: 'APR-01 – APR-08', feature: 'APPROVALS' },
      { key: 'projects', path: '/projects', icon: IconFolders, phase: 1, features: 'PRJ-01 – PRJ-12' },
      { key: 'team', path: '/team', icon: IconUsers, phase: 1, features: 'TEAM-01 – TEAM-08' },
      { key: 'clients', path: '/clients', icon: IconBuildingStore, phase: 1, features: 'PRJ-09' },
      { key: 'tags', path: '/tags', icon: IconTags, phase: 1, features: 'PRJ-10' },
      { key: 'invoices', path: '/invoices', icon: IconFileInvoice, phase: 4, features: 'INV-01 – INV-09', feature: 'INVOICING', visible: admins },
      { key: 'settings', path: '/settings', icon: IconSettings, phase: 1, features: 'WS-01 – WS-06', visible: admins },
      { key: 'billing', path: '/billing', icon: IconCreditCard, phase: 6, features: 'PLAN-01 – PLAN-08', visible: owners },
    ],
  },
];

export const allNavItems: NavItem[] = navigation.flatMap((group) => group.items);

export function isNavItemVisible(item: NavItem, ctx: CurrentWorkspace): boolean {
  if (item.feature && !ctx.hasFeature(item.feature)) return false;
  return item.visible ? item.visible(ctx) : true;
}
